import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { UserMinus, Printer, EnvelopeSimple, ArrowCounterClockwise, MagnifyingGlass, FileText, CaretUpDown, Check } from '@phosphor-icons/react';
import { printHTML, downloadWord } from '../utils/printHelper';
import { confirmDialog } from '@/components/ConfirmDialog';
import { formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '@/components/DateInput';
import {
  generateRjesenjePrestanku,
  generateRjesenjeOtkazNeopravdano,
  generateOtkazOdZaposlenog,
  generateSporazumPrestanak,
  calcDurationYMD,
} from './PrijavaRadnikaPage';

const OTKAZ_RAZLOZI = [
  { value: 'istek_ugovora', label: 'Istek ugovora o radu (po sili zakona)' },
  { value: 'neopravdano_izostajanje', label: 'Neopravdano izostajanje sa posla (3 dana uzastopno)' },
  { value: 'sporazumni_raskid', label: 'Sporazumni raskid' },
  { value: 'otkaz_zaposlenog', label: 'Otkaz od strane zaposlenog' },
  { value: 'drugi_razlog', label: 'Drugi razlog' },
];


// Which document template is used for each termination reason
function pickTerminationHtml(razlog, entry, client) {
  switch (razlog) {
    case 'neopravdano_izostajanje':
      return { html: generateRjesenjeOtkazNeopravdano(entry, client), baseName: 'Rjesenje_otkaz_neopravdano' };
    case 'otkaz_zaposlenog':
      return { html: generateOtkazOdZaposlenog(entry, client), baseName: 'Rjesenje_prestanak_otkaz_zaposlenog' };
    case 'sporazumni_raskid':
      return { html: generateSporazumPrestanak(entry, client), baseName: 'Sporazum_o_prestanku_radnog_odnosa' };
    case 'istek_ugovora':
    default:
      return { html: generateRjesenjePrestanku(entry, client), baseName: 'Rjesenje_prestanak' };
  }
}

export default function OdjavaRadnikaPage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [jmbSearch, setJmbSearch] = useState('');
  const [workerPickerOpen, setWorkerPickerOpen] = useState(false);
  const [filterClient, setFilterClient] = useState('all');
  const [filterRazlog, setFilterRazlog] = useState('all');
  const [showOdjavaDialog, setShowOdjavaDialog] = useState(false);
  const [activeWorkers, setActiveWorkers] = useState([]);
  const [odjavaForm, setOdjavaForm] = useState({
    entry_id: '',
    datum_odjave: '',
    razlog_odjave: 'istek_ugovora',
    datum_izostanaka: '',
    datum_otkaza_zaposlenog: '',
    broj_ovjere_otkaza: '',
  });
  // When razlog is "istek_ugovora" (contract expiry), auto-populate datum_odjave with the
  // selected worker's datum_isteka from the Prijava — because the termination date for a
  // contract-expiry termination is BY LAW the day the fixed-term contract runs out.
  const updateOdjavaForm = (patch) => {
    setOdjavaForm(prev => {
      const next = { ...prev, ...patch };
      const razlog = next.razlog_odjave;
      const worker = activeWorkers.find(w => w.id === next.entry_id);
      if (razlog === 'istek_ugovora' && worker && worker.datum_isteka) {
        const triggeredByWorkerOrRazlog = ('entry_id' in patch) || ('razlog_odjave' in patch);
        if (triggeredByWorkerOrRazlog) {
          next.datum_odjave = worker.datum_isteka;
        }
      }
      return next;
    });
  };

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eRes, cRes] = await Promise.all([
        api.get('/prijava-radnika', { params: { status: 'odjavljen' } }),
        api.get('/clients'),
      ]);
      // Sort by order of entry — most recently odjavljen worker first.
      // Prefers updated_at (set by PATCH when odjava is saved), falls back to created_at or datum_odjave.
      const list = (eRes.data || []).slice().sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || a.datum_odjave || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || b.datum_odjave || 0).getTime();
        return tb - ta;
      });
      setEntries(list);
      setClients(cRes.data || []);
    } catch (e) {
      toast.error('Greška pri učitavanju odjavljenih radnika');
    }
  };

  const openOdjavaDialog = async () => {
    try {
      const r = await api.get('/prijava-radnika', { params: { status: 'aktivan' } });
      setActiveWorkers(r.data || []);
      const today = new Date().toISOString().slice(0, 10);
      setOdjavaForm({ entry_id: '', datum_odjave: today, razlog_odjave: 'istek_ugovora', datum_izostanaka: '', datum_otkaza_zaposlenog: '', broj_ovjere_otkaza: '' });
      setShowOdjavaDialog(true);
    } catch (e) {
      toast.error('Greška pri učitavanju aktivnih radnika');
    }
  };

  const handleOdjaviti = async () => {
    if (!odjavaForm.entry_id) { toast.error('Izaberite radnika'); return; }
    if (!odjavaForm.datum_odjave) { toast.error('Unesite datum odjave'); return; }
    setSaving(true);
    try {
      const payload = {
        status: 'odjavljen',
        datum_odjave: odjavaForm.datum_odjave,
        razlog_odjave: odjavaForm.razlog_odjave,
      };
      if (odjavaForm.razlog_odjave === 'neopravdano_izostajanje' && odjavaForm.datum_izostanaka) {
        payload.datum_izostanaka = odjavaForm.datum_izostanaka;
      }
      if (odjavaForm.razlog_odjave === 'otkaz_zaposlenog') {
        if (odjavaForm.datum_otkaza_zaposlenog) payload.datum_otkaza_zaposlenog = odjavaForm.datum_otkaza_zaposlenog;
        if (odjavaForm.broj_ovjere_otkaza) payload.broj_ovjere_otkaza = odjavaForm.broj_ovjere_otkaza;
      }
      await api.patch(`/prijava-radnika/${odjavaForm.entry_id}`, payload);
      toast.success('Radnik uspješno odjavljen');
      setShowOdjavaDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška pri odjavi');
    } finally {
      setSaving(false);
    }
  };

  const handleVratiAktivan = async (entry) => {
    if (!(await confirmDialog(`Vratiti radnika "${entry.prezime} ${entry.ime}" u aktivni status? Datum odjave će biti obrisan.`))) return;
    try {
      await api.patch(`/prijava-radnika/${entry.id}`, { status: 'aktivan', datum_odjave: '' });
      toast.success('Radnik vraćen u aktivni status');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška');
    }
  };

  const handlePrint = (entry, overrideRazlog = null) => {
    const client = clients.find(c => c.id === entry.client_id);
    const razlog = overrideRazlog || entry.razlog_odjave || 'istek_ugovora';
    const { html } = pickTerminationHtml(razlog, entry, client);
    printHTML(html);
  };

  const handleWord = (entry, overrideRazlog = null) => {
    const client = clients.find(c => c.id === entry.client_id);
    const razlog = overrideRazlog || entry.razlog_odjave || 'istek_ugovora';
    const { html, baseName } = pickTerminationHtml(razlog, entry, client);
    const name = `${baseName}_${(entry.prezime || '').replace(/\s/g, '_')}_${(entry.ime || '').replace(/\s/g, '_')}.docx`;
    downloadWord(html, name);
  };

  const handleSendEmail = async (entry) => {
    const client = clients.find(c => c.id === entry.client_id);
    if (!client?.email) { toast.error('Klijent nema email adresu'); return; }
    if (!(await confirmDialog(`Poslati Rješenje o prestanku za ${entry.prezime} ${entry.ime} na ${client.email}?`))) return;
    try {
      const r = await api.post(`/prijava-radnika/${entry.id}/send-rjesenje-prestanku`);
      toast.success(r.data.message || 'Email poslat');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška pri slanju');
    }
  };

  // ===== Filters =====
  const filtered = entries.filter(e => {
    if (filterClient !== 'all' && e.client_id !== filterClient) return false;
    if (filterRazlog !== 'all' && (e.razlog_odjave || 'istek_ugovora') !== filterRazlog) return false;
    // JMB search — strip whitespace so pasted values match stored digits-only JMB
    const entryJmb = (e.jmb || '').replace(/\s+/g, '');
    if (jmbSearch && jmbSearch.trim()) {
      const needle = jmbSearch.trim().replace(/\s+/g, '');
      if (!entryJmb.includes(needle)) return false;
    }
    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      const name = `${e.prezime || ''} ${e.ime || ''}`.toLowerCase();
      const rm = (e.naziv_radnog_mjesta || '').toLowerCase();
      // Allow the primary search box to also match JMB (so single-field flow still works)
      if (!name.includes(q) && !entryJmb.includes(q) && !rm.includes(q)) return false;
    }
    return true;
  });

  const getClientName = (cid) => {
    const c = clients.find(c => c.id === cid);
    if (!c) return '-';
    return c.skraceni_naziv || c.naziv_firme || '-';
  };
  const getRazlogLabel = (v) => OTKAZ_RAZLOZI.find(r => r.value === v)?.label || (v ? v : 'Istek ugovora o radu (po sili zakona)');

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="odjava-radnika-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light text-slate-900">Odjava radnika</h1>
          <p className="text-sm text-slate-500 mt-1">Evidencija prestanka radnog odnosa i generisanje zakonskih obrazaca.</p>
        </div>
        <Button
          onClick={openOdjavaDialog}
          className="bg-red-600 hover:bg-red-700 text-white rounded-md px-4 py-2 font-medium"
          data-testid="odjava-open-dialog-button"
        >
          <UserMinus size={16} className="mr-2" />
          Odjavi radnika
        </Button>
      </div>

      {/* Filteri */}
      <Card className="p-4 border border-slate-200 bg-white rounded-md">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ime, prezime, radno mjesto ili JMB..."
              className="pl-10 bg-white border-slate-300 text-sm h-10"
              data-testid="odjava-search-input"
            />
          </div>
          <div className="relative">
            <Input
              value={jmbSearch}
              onChange={(e) => setJmbSearch(e.target.value.replace(/[^\d]/g, ''))}
              placeholder="JMB radnika (13 cifara)"
              className="bg-white border-slate-300 text-sm h-10 font-mono"
              inputMode="numeric"
              maxLength={13}
              data-testid="odjava-jmb-search-input"
            />
            {jmbSearch && (
              <button
                type="button"
                onClick={() => setJmbSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs"
                data-testid="odjava-jmb-clear"
                title="Očisti"
              >
                ✕
              </button>
            )}
          </div>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="h-10 bg-white border-slate-300" data-testid="odjava-filter-client"><SelectValue placeholder="Klijent" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Svi klijenti</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.skraceni_naziv || c.naziv_firme}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterRazlog} onValueChange={setFilterRazlog}>
            <SelectTrigger className="h-10 bg-white border-slate-300" data-testid="odjava-filter-razlog"><SelectValue placeholder="Razlog" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Svi razlozi</SelectItem>
              {OTKAZ_RAZLOZI.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <span>Ukupno: <strong className="text-slate-800">{filtered.length}</strong> odjavljen{filtered.length === 1 ? '' : 'ih'} radnik{filtered.length === 1 ? '' : 'a'}</span>
          {(search || jmbSearch || filterClient !== 'all' || filterRazlog !== 'all') && (
            <button
              onClick={() => { setSearch(''); setJmbSearch(''); setFilterClient('all'); setFilterRazlog('all'); }}
              className="text-blue-600 hover:underline"
              data-testid="odjava-reset-filters"
            >
              Resetuj filtere
            </button>
          )}
        </div>
      </Card>

      {/* Tabela */}
      <Card className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Zaposleni</th>
                <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Klijent</th>
                <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Radno mjesto</th>
                <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Datum sticanja</th>
                <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Datum odjave</th>
                <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Staž (GG.MM.DD)</th>
                <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Razlog</th>
                <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500 text-right">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-400">
                    <FileText size={28} className="mx-auto mb-2 text-slate-300" />
                    Nema odjavljenih radnika koji odgovaraju filteru.
                  </td>
                </tr>
              ) : filtered.map(e => {
                const razlog = e.razlog_odjave || 'istek_ugovora';
                return (
                  <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`odjava-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{e.prezime} {e.ime}</div>
                      <div className="text-xs text-slate-500 font-mono">{e.jmb || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{getClientName(e.client_id)}</td>
                    <td className="px-4 py-3 text-slate-700">{e.naziv_radnog_mjesta || '-'}</td>
                    <td className="px-4 py-3 text-slate-700">{formatDatePrint(e.datum_sticanja) || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded text-xs font-medium">
                        {formatDatePrint(e.datum_odjave) || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-700">{calcDurationYMD(e.datum_sticanja, e.datum_odjave) || '-'}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{getRazlogLabel(razlog)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end flex-wrap">
                        {(() => {
                          // Button color + label depends on razlog. Tailwind classes must be literal
                          // strings for the JIT to include them, so we map to full className strings.
                          const styleMap = {
                            neopravdano_izostajanje: { print: 'p-1.5 hover:bg-orange-100 rounded text-orange-700', word: 'p-1.5 hover:bg-orange-50 rounded text-orange-600 text-[10px] font-semibold', label: 'Rješenje o otkazu (neopravdano)' },
                            otkaz_zaposlenog: { print: 'p-1.5 hover:bg-violet-100 rounded text-violet-700', word: 'p-1.5 hover:bg-violet-50 rounded text-violet-600 text-[10px] font-semibold', label: 'Rješenje o prestanku (otkaz zaposlenog)' },
                            sporazumni_raskid: { print: 'p-1.5 hover:bg-teal-100 rounded text-teal-700', word: 'p-1.5 hover:bg-teal-50 rounded text-teal-600 text-[10px] font-semibold', label: 'Sporazum o prestanku' },
                            istek_ugovora: { print: 'p-1.5 hover:bg-red-100 rounded text-red-700', word: 'p-1.5 hover:bg-red-50 rounded text-red-600 text-[10px] font-semibold', label: 'Rješenje o prestanku (istek ugovora)' },
                            drugi_razlog: { print: 'p-1.5 hover:bg-red-100 rounded text-red-700', word: 'p-1.5 hover:bg-red-50 rounded text-red-600 text-[10px] font-semibold', label: 'Rješenje o prestanku' },
                          };
                          const s = styleMap[razlog] || styleMap.istek_ugovora;
                          return (
                            <>
                              <button
                                onClick={() => handlePrint(e)}
                                className={s.print}
                                title={`Štampaj ${s.label}`}
                                data-testid={`odjava-print-${e.id}`}
                              >
                                <Printer size={15} />
                              </button>
                              <button
                                onClick={() => handleWord(e)}
                                className={s.word}
                                title={`Word ${s.label}`}
                                data-testid={`odjava-word-${e.id}`}
                              >
                                DOC
                              </button>
                            </>
                          );
                        })()}
                        <button
                          onClick={() => handleSendEmail(e)}
                          className="p-1.5 hover:bg-emerald-100 rounded text-emerald-700"
                          title="Pošalji Rješenje na email klijenta"
                          data-testid={`odjava-email-${e.id}`}
                        >
                          <EnvelopeSimple size={15} />
                        </button>
                        <button
                          onClick={() => handleVratiAktivan(e)}
                          className="p-1.5 hover:bg-slate-200 rounded text-slate-600"
                          title="Vrati u aktivne"
                          data-testid={`odjava-undo-${e.id}`}
                        >
                          <ArrowCounterClockwise size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Odjava Dialog */}
      <Dialog open={showOdjavaDialog} onOpenChange={setShowOdjavaDialog}>
        <DialogContent className="max-w-xl" data-testid="odjava-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium text-slate-900 flex items-center gap-2">
              <UserMinus size={20} className="text-red-600" />
              Odjava radnika
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Radnik *</Label>
              {(() => {
                const selected = activeWorkers.find(w => w.id === odjavaForm.entry_id);
                return (
                  <Popover open={workerPickerOpen} onOpenChange={setWorkerPickerOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        aria-expanded={workerPickerOpen}
                        className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                        data-testid="odjava-entry-select"
                      >
                        <span className={`truncate text-left ${selected ? 'text-slate-900' : 'text-slate-400'}`}>
                          {selected
                            ? `${selected.prezime} ${selected.ime} — JMB ${selected.jmb || '-'} — ${getClientName(selected.client_id)}`
                            : 'Izaberite aktivnog radnika...'}
                        </span>
                        <CaretUpDown size={16} className="text-slate-500 shrink-0 ml-2" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-w-none" align="start">
                      <Command
                        filter={(value, query) => {
                          if (!query) return 1;
                          return value.toLowerCase().includes(query.trim().toLowerCase()) ? 1 : 0;
                        }}
                      >
                        <CommandInput
                          placeholder="Pretraga po JMB-u, imenu, prezimenu ili klijentu..."
                          data-testid="odjava-entry-search-input"
                        />
                        <CommandList className="max-h-[320px]">
                          <CommandEmpty>Nema radnika koji odgovara pretrazi.</CommandEmpty>
                          <CommandGroup>
                            {activeWorkers.map(w => {
                              const jmbStripped = (w.jmb || '').replace(/\s+/g, '');
                              // Include JMB (both as-stored and digits-only), name, client, radno mjesto in the search haystack
                              const haystack = [
                                w.prezime || '',
                                w.ime || '',
                                `${w.prezime || ''} ${w.ime || ''}`,
                                `${w.ime || ''} ${w.prezime || ''}`,
                                w.jmb || '',
                                jmbStripped,
                                getClientName(w.client_id),
                                w.naziv_radnog_mjesta || '',
                              ].join(' ');
                              const label = `${w.prezime} ${w.ime} — ${getClientName(w.client_id)} (${w.naziv_radnog_mjesta || '-'})`;
                              return (
                                <CommandItem
                                  key={w.id}
                                  value={haystack}
                                  onSelect={() => {
                                    updateOdjavaForm({ entry_id: w.id });
                                    setWorkerPickerOpen(false);
                                  }}
                                  data-testid={`odjava-entry-option-${w.id}`}
                                >
                                  <Check
                                    size={14}
                                    className={`mr-2 ${odjavaForm.entry_id === w.id ? 'opacity-100 text-red-600' : 'opacity-0'}`}
                                    weight="bold"
                                  />
                                  <div className="flex flex-col flex-1 min-w-0">
                                    <div className="font-medium text-slate-900 truncate">{w.prezime} {w.ime}</div>
                                    <div className="text-xs text-slate-500 font-mono">JMB: {w.jmb || '-'}</div>
                                    <div className="text-xs text-slate-500 truncate">{getClientName(w.client_id)} • {w.naziv_radnog_mjesta || '-'}</div>
                                  </div>
                                </CommandItem>
                              );
                            })}
                            {activeWorkers.length === 0 && (
                              <div className="p-3 text-sm text-slate-500">Nema aktivnih radnika.</div>
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Datum odjave *</Label>
              <DateInput
                value={odjavaForm.datum_odjave}
                onChange={e => setOdjavaForm({ ...odjavaForm, datum_odjave: e.target.value })}
                className="bg-white border-slate-300 text-sm"
                data-testid="odjava-datum-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Razlog odjave *</Label>
              <Select value={odjavaForm.razlog_odjave} onValueChange={v => updateOdjavaForm({ razlog_odjave: v })}>
                <SelectTrigger className="bg-white border-slate-300 text-sm" data-testid="odjava-razlog-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OTKAZ_RAZLOZI.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {odjavaForm.razlog_odjave === 'neopravdano_izostajanje' && (
              <div className="space-y-1">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Datumi 3 uzastopna izostanka</Label>
                <Input
                  value={odjavaForm.datum_izostanaka}
                  onChange={e => setOdjavaForm({ ...odjavaForm, datum_izostanaka: e.target.value })}
                  placeholder="npr. 05.10.2026, 06.10.2026, 07.10.2026"
                  className="bg-white border-slate-300 text-sm"
                  data-testid="odjava-datumi-izostanaka"
                />
                <p className="text-xs text-slate-500 mt-1">Ovi datumi će se automatski ispisati u Rješenju o otkazu po članu 172 st. 1 t. 2 al. 3 Zakona o radu.</p>
              </div>
            )}
            {odjavaForm.razlog_odjave === 'otkaz_zaposlenog' && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Datum otkaza (zaposleni ga je podnio)</Label>
                  <DateInput
                    value={odjavaForm.datum_otkaza_zaposlenog}
                    onChange={e => setOdjavaForm({ ...odjavaForm, datum_otkaza_zaposlenog: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="odjava-datum-otkaza-zaposlenog"
                  />
                  <p className="text-xs text-slate-500 mt-1">Datum kad je zaposleni dostavio pisani otkaz ugovora o radu (prethodno ovjeren kod notara).</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Broj ovjere kod notara</Label>
                  <Input
                    value={odjavaForm.broj_ovjere_otkaza}
                    onChange={e => setOdjavaForm({ ...odjavaForm, broj_ovjere_otkaza: e.target.value })}
                    placeholder="npr. UOP-II:123/2026"
                    className="bg-white border-slate-300 text-sm"
                    data-testid="odjava-broj-ovjere-otkaza"
                  />
                  <p className="text-xs text-slate-500 mt-1">Opciono — upisuje se u obrazloženje Rješenja po članu 166 st. 5 Zakona o radu.</p>
                </div>
              </>
            )}
            <div className="flex gap-3 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowOdjavaDialog(false)}
                className="rounded-md px-4 py-2 font-medium"
                data-testid="odjava-cancel-button"
                disabled={saving}
              >
                Odustani
              </Button>
              <Button
                type="button"
                onClick={handleOdjaviti}
                className="bg-red-600 hover:bg-red-700 text-white rounded-md px-4 py-2 font-medium"
                data-testid="odjava-submit-button"
                disabled={saving}
              >
                <UserMinus size={16} className="mr-2" />
                {saving ? 'Odjavljujem...' : 'Odjavi radnika'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
